/**
 * Google Ads monthly recap API client (P3.1c).
 *
 * Backend routes:
 *   GET  /google-ads/monthly/recaps?tenant_id=&limit=
 *   GET  /google-ads/monthly/recaps/{report_id}
 *   POST /google-ads/monthly/trigger?tenant_id=&period_start=&period_end=
 *
 * Master flag: GOOGLE_ADS_MONTHLY_MGMT_ENABLED
 */
import { apiClient } from './apiClient';

export interface RecapSummary {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  llm_cost_usd: number;
  created_at: string;
}

export interface RecapDetail extends RecapSummary {
  narrative_md: string;
  metrics_json: {
    customer?: {
      spend_usd?: number;
      clicks?: number;
      impressions?: number;
      conversions?: number;
      cost_per_conversion_usd?: number;
    };
    campaigns?: Array<{
      id: string;
      name: string;
      status: string;
      spend_usd: number;
      conversions: number;
      cost_per_conversion_usd: number;
    }>;
    active_campaign_count?: number;
    /** P5.2 — top competitors from Auction Insights aggregated over the period. */
    competitors?: {
      top_competitors?: Array<{
        domain: string;
        avg_impression_share?: number | null;
        avg_overlap_rate?: number | null;
        avg_outranking_share?: number | null;
        avg_top_of_page_rate?: number | null;
        avg_position_above_rate?: number | null;
        observations?: number;
        first_seen?: string | null;
        last_seen?: string | null;
      }>;
      competitor_count?: number;
    };
  };
  weekly_rollup_json: {
    total_runs?: number;
    runs_by_status?: Record<string, number>;
    search_terms_flagged?: number;
    ads_paused?: number;
    keywords_paused?: number;
    approval_items_created?: number;
    total_llm_cost_usd?: number;
    audit_log?: {
      total_writes?: number;
      by_operation?: Record<string, Record<string, number>>;
    };
  };
}

export interface TriggerResult {
  tenant_id: string;
  period_start: string;
  period_end: string;
  report_id: string;
  narrative_chars: number;
  llm_cost_usd: number;
}

export interface KpiMetricDelta {
  absolute: number | null;
  percent: number | null;
}

export interface KpiMetrics {
  spend_usd?: number;
  clicks?: number;
  impressions?: number;
  conversions?: number;
  cost_per_conversion_usd?: number | null;
  ctr?: number | null;
  cpc_usd?: number | null;
  conversion_rate?: number | null;
  impression_share?: number | null;
  lost_impression_share_budget?: number | null;
  lost_impression_share_rank?: number | null;
  available?: boolean;
}

export interface KpiNextAction {
  action: string;
  risk_level: 'low' | 'medium' | 'high';
  requires_approval: boolean;
  rationale: string;
}

export interface KpiDiagnosticSummary {
  id: string;
  tenant_id: string;
  period_start: string;
  period_end: string;
  previous_period_start: string;
  previous_period_end: string;
  created_at: string;
}

export interface KpiDiagnosticDetail extends KpiDiagnosticSummary {
  report_id?: string;
  customer_id: string;
  current: KpiMetrics;
  previous: KpiMetrics;
  deltas: Record<string, KpiMetricDelta>;
  known_facts: string[];
  likely_causes: string[];
  next_actions: KpiNextAction[];
  warnings: string[];
  client_safe_summary: string;
  narrative_md: string;
}

export async function listMonthlyRecaps(
  tenantId: string,
  limit = 12,
): Promise<RecapSummary[]> {
  const { data } = await apiClient.get<RecapSummary[]>(
    '/google-ads/monthly/recaps',
    { params: { tenant_id: tenantId, limit } },
  );
  return data;
}

export async function getMonthlyRecap(
  reportId: string,
): Promise<RecapDetail> {
  const { data } = await apiClient.get<RecapDetail>(
    `/google-ads/monthly/recaps/${reportId}`,
  );
  return data;
}

export async function triggerMonthlyRecap(params: {
  tenantId: string;
  periodStart?: string;
  periodEnd?: string;
}): Promise<TriggerResult> {
  const { data } = await apiClient.post<TriggerResult>(
    '/google-ads/monthly/trigger',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        period_start: params.periodStart,
        period_end: params.periodEnd,
      },
    },
  );
  return data;
}

/**
 * Download the client-safe monthly recap export (P3.1e).
 *
 * Backend: GET /google-ads/monthly/recaps/{report_id}/export
 *   ?tenant_id=<domain>&format=html|md|pdf|pptx → binary/text file response
 *   with Content-Disposition: attachment (media_type text/html,
 *   text/markdown, application/pdf, or the PowerPoint presentation type).
 *   This is the client-facing projection — no internal metrics.
 */
export async function downloadMonthlyRecapExport(
  reportId: string,
  tenantId: string,
  format: 'html' | 'md' | 'pdf' | 'pptx' = 'html',
): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    `/google-ads/monthly/recaps/${reportId}/export`,
    {
      params: { tenant_id: tenantId, format },
      responseType: 'blob',
    },
  );
  return response.data;
}

export async function runKpiDiagnostics(params: {
  tenantId: string;
  periodStart?: string;
  periodEnd?: string;
  previousPeriodStart?: string;
  previousPeriodEnd?: string;
}): Promise<KpiDiagnosticDetail> {
  const { data } = await apiClient.post<KpiDiagnosticDetail>(
    '/google-ads/monthly/kpi-diagnostics/run',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        period_start: params.periodStart,
        period_end: params.periodEnd,
        previous_period_start: params.previousPeriodStart,
        previous_period_end: params.previousPeriodEnd,
      },
      timeout: 60_000,
    },
  );
  return data;
}

export async function listKpiDiagnosticReports(
  tenantId: string,
  limit = 12,
): Promise<KpiDiagnosticSummary[]> {
  const { data } = await apiClient.get<KpiDiagnosticSummary[]>(
    '/google-ads/monthly/kpi-diagnostics/reports',
    { params: { tenant_id: tenantId, limit } },
  );
  return data;
}

export async function getKpiDiagnosticReport(
  reportId: string,
): Promise<KpiDiagnosticDetail> {
  const { data } = await apiClient.get<KpiDiagnosticDetail>(
    `/google-ads/monthly/kpi-diagnostics/reports/${reportId}`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// P3.2 — Budget reallocation

export type BudgetAdjustmentRule = 'art_relearn_cap' | 'expert_tcpa_sync';
// ---------------------------------------------------------------------------

export interface BudgetProposal {
  campaign_id: string;
  campaign_name: string;
  campaign_resource_name: string;
  budget_resource_name: string;
  current_amount_micros: number;
  proposed_amount_micros: number;
  delta_micros: number;
  reason: string;
  /** True when a proposal crosses the ±15% smart-bidding relearn threshold. */
  relearn_risk?: boolean;
  bidding_strategy_type?: string;
  current_target_cpa_micros?: number | null;
  proposed_target_cpa_micros?: number | null;
  bid_strategy_note?: string | null;
  spend_micros?: number;
  clicks?: number;
  conversions?: number;
  cost_per_conversion_micros?: number;
  uncapped_want_amount_micros?: number;
  was_budget_clamped?: boolean;
}

export interface NewCampaignRecommendation {
  title: string;
  summary: string;
  reason: string;
  rollover_micros: number;
  cycle_budget_micros: number;
  spend_micros: number;
  rollover_ratio: number;
  safe_reallocation_headroom_micros: number;
  safe_reallocation_headroom_ratio: number;
  campaign_count: number;
  total_clicks: number;
  total_conversions: number;
  suggested_campaign_theme: string;
  suggested_monthly_budget_low_micros?: number | null;
  suggested_monthly_budget_high_micros?: number | null;
  signals: string[];
}

export interface BudgetAnalyzeResponse {
  tenant_id: string;
  period_start: string;
  period_end: string;
  mode: 'warn' | 'soft_auto';
  budget_adjustment_rule: BudgetAdjustmentRule;
  summary: string;
  proposals: BudgetProposal[];
  approval_id: string | null;
  skipped_reason: string | null;
  new_campaign_recommendation?: NewCampaignRecommendation | null;
  analyzed_at?: string | null;
  facts_source?: 'live' | 'stub';
}

export interface BudgetApplyResponse {
  request_id: string;
  flow: string;
  applied: number;
  skipped: number;
  errors_recorded: number;
}

export type BudgetPacingStatus =
  | 'no_data'
  | 'on_track'
  | 'over_pacing'
  | 'under_pacing'
  | 'over_spend';

export interface BudgetPacingTotals {
  budget_micros: number;
  budget_usd: number;
  spent_micros: number;
  spent_usd: number;
  remaining_micros: number;
  remaining_usd: number;
  suggested_daily_micros: number;
  suggested_daily_usd: number;
  ideal_daily_micros: number;
  ideal_daily_usd: number;
  incoming_cumulative_micros: number;
  incoming_cumulative_usd: number;
  monthly_rollover_micros: number;
  monthly_rollover_usd: number;
  cumulative_rollover_micros: number;
  cumulative_rollover_usd: number;
  pacing_status: BudgetPacingStatus;
  budget_source?: 'planner_contract' | 'daily_cap_fallback' | 'planner_no_google';
  spend_source?: 'planner_recorded' | 'live_google_ads';
  daily_cap_budget_micros?: number;
  live_spent_micros?: number;
  live_spent_usd?: number;
  // Google LSA broken out as a separate line (part of the combined budget_usd
  // total). Present only when the client has a Google LSA budget.
  lsa_budget_usd?: number;
  lsa_spent_usd?: number;
  lsa_remaining_usd?: number;
}

export interface BudgetPacingCampaignRow {
  campaign_id: string;
  campaign_name: string;
  campaign_resource_name: string;
  campaign_status: string;
  bidding_strategy_type?: string;
  target_cpa_micros?: number | null;
  target_cpa_usd?: number | null;
  budget_resource_name: string;
  daily_budget_micros: number;
  daily_budget_usd: number;
  google_monthly_cap_micros?: number;
  google_monthly_cap_usd?: number;
  daily_spend_cap_micros?: number;
  daily_spend_cap_usd?: number;
  active_day_average_micros?: number;
  active_day_average_usd?: number;
  active_service_days_per_month?: number;
  ad_schedule_source?: 'google_ads_ad_schedule' | 'no_ad_schedule_found' | string;
  active_weekdays?: string[];
  ad_schedule_pacing_evidence?: string;
  cycle_budget_micros: number;
  cycle_budget_usd: number;
  spend_micros: number;
  spend_usd: number;
  /** Live Ads spend since the last Mon/Thu 08:00 Central tick (account dates). */
  since_cron_spend_micros?: number;
  since_cron_spend_usd?: number;
  remaining_micros: number;
  remaining_usd: number;
  suggested_daily_micros: number;
  suggested_daily_usd: number;
  ideal_daily_micros: number;
  ideal_daily_usd: number;
  monthly_rollover_micros: number;
  monthly_rollover_usd: number;
  clicks: number;
  conversions: number;
  advertising_channel_type?: string;
  pacing_status: BudgetPacingStatus;
}

export interface BudgetPacingPlatformRow {
  platform: string; // "Google Ads" | "Google LSA" | "Meta Ads" | "Bing" | ...
  budget_usd: number;
  spent_usd: number;
  remaining_usd: number;
  /**
   * When this spend figure was last written, ISO-8601, for HAND-ENTERED
   * platforms only. Null for Google Ads / LSA by design, not by omission.
   *
   * Sourced from contract_ad_spend.synced_at, which advances when a figure
   * CHANGES (the mirror upserts only differing rows), not when it is verified.
   * For a hand-entered platform that is the same thing as "when a person last
   * typed it". For Google it is not: a paused or zero-spend account's row sits
   * unchanged for weeks while its data is current, so an age there would be a
   * false staleness warning.
   */
  spend_recorded_at?: string | null;
}

export interface BudgetPacingCycle {
  label: string;
  start: string;
  end: string;
  days_total: number;
  days_left: number;
  /** Planner's divisor convention for this cycle; absent on older backends. */
  days_left_inclusive?: boolean | null;
  days_elapsed: number;
  tracked: boolean;
  /** Per-platform breakdown for the cycle (Planner mirror); empty for unmapped. */
  platforms?: BudgetPacingPlatformRow[];
  totals: BudgetPacingTotals;
  rows: BudgetPacingCampaignRow[];
}

export interface SinceCronWindow {
  start: string;
  end: string;
  last_run_at: string;
  next_run_at: string;
  /** False when the since-cron Ads read failed — do not paint Spent as $0. */
  available?: boolean;
}

export interface BudgetPacingResponse {
  tenant_id: string;
  customer_id: string;
  billing_cycle_start_day: 1 | 15;
  generated_at: string;
  current_cycle_index: number;
  budget_source?: 'planner_contract' | 'daily_cap_fallback' | 'planner_no_google';
  spend_source?: 'planner_recorded' | 'live_google_ads';
  /** True when the client has a Google LSA budget → render the separate LSA line. */
  has_lsa?: boolean;
  /** IANA zone from Google Ads `customer.time_zone` — when daily caps reset. */
  account_time_zone?: string | null;
  /** Last Mon/Thu 08:00 Central → now, as account-timezone civil dates. */
  since_cron?: SinceCronWindow | null;
  /** Planner LSA CID when linked. Core does not write that account. */
  lsa_customer_id?: string | null;
  /** Shared-account partition + both CIDs from Planner. */
  campaign_scope?: BudgetPacingCampaignScope | null;
  cycles: BudgetPacingCycle[];
}

export interface BudgetPacingCampaignScope {
  included_campaign_ids?: string[] | null;
  campaign_name_filter?: string | null;
  ads_customer_id?: string | null;
  lsa_customer_id?: string | null;
  error?: string | null;
  kept?: number;
  dropped?: number;
}

export interface PacingAccountScope {
  ads_customer_id?: string | null;
  lsa_customer_id?: string | null;
  campaign_name_filter?: string | null;
  included_campaign_ids?: string[] | null;
  campaign_scope_error?: string | null;
  has_google_ads_budget_row?: boolean;
  tenant_customer_id?: string | null;
  planner_mapped?: boolean;
  /** Core's own tenant-row state for the LSA writer. */
  tenant_lsa_customer_id?: string | null;
  lsa_autopace?: boolean | null;
  /** Display only: Google Ads and LSA combined in the daily totals. */
  combined_totals?: boolean | null;
  /** Server clock of the save; the Budgets page guards its post-save refetch with it. */
  saved_at?: string | null;
}

export async function getBudgetPacing(params: {
  tenantId: string;
  cycleStartDay?: 1 | 15;
  historyCycles?: number;
}): Promise<BudgetPacingResponse> {
  const { data } = await apiClient.get<BudgetPacingResponse>(
    '/google-ads/monthly/budget-pacing',
    {
      params: {
        tenant_id: params.tenantId,
        cycle_start_day: params.cycleStartDay ?? 1,
        history_cycles: params.historyCycles ?? 6,
      },
      // 'All history' can be 30+ cycles; older cycles skip live GAQL but the
      // current window still fetches per cycle.
      timeout: 45_000,
    },
  );
  return data;
}

export async function analyzeBudgetReallocation(params: {
  tenantId: string;
  mode: 'warn' | 'soft_auto';
  budgetAdjustmentRule?: BudgetAdjustmentRule;
  periodStart?: string;
  periodEnd?: string;
}): Promise<BudgetAnalyzeResponse> {
  const { data } = await apiClient.post<BudgetAnalyzeResponse>(
    '/google-ads/monthly/budget-reallocation/analyze',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        mode: params.mode,
        budget_adjustment_rule: params.budgetAdjustmentRule ?? 'art_relearn_cap',
        period_start: params.periodStart,
        period_end: params.periodEnd,
      },
    },
  );
  return data;
}

export async function applyBudgetReallocation(
  requestId: string,
): Promise<BudgetApplyResponse> {
  const { data } = await apiClient.post<BudgetApplyResponse>(
    `/google-ads/monthly/budget-reallocation/approvals/${requestId}/apply`,
  );
  return data;
}

export interface BudgetVerifyRow {
  campaign_id: string;
  campaign_name: string;
  budget_resource_name: string;
  written_amount_micros: number;
  live_amount_micros: number | null;
  status: 'applied' | 'mismatch' | 'unavailable' | string;
}

export interface BudgetVerifyResponse {
  request_id: string;
  rows: BudgetVerifyRow[];
}

export interface BudgetUndoResponse {
  request_id: string;
  undone: number;
  target?: number;
  partial?: boolean;
  refused_reason: string | null;
  refused_rows: string[];
}

export async function verifyBudgetReallocation(
  requestId: string,
): Promise<BudgetVerifyResponse> {
  const { data } = await apiClient.post<BudgetVerifyResponse>(
    `/google-ads/monthly/budget-reallocation/approvals/${requestId}/verify`,
  );
  return data;
}

export async function undoBudgetReallocation(
  requestId: string,
): Promise<BudgetUndoResponse> {
  const { data } = await apiClient.post<BudgetUndoResponse>(
    `/google-ads/monthly/budget-reallocation/approvals/${requestId}/undo`,
  );
  return data;
}

export interface AutonomousPacingAdjustment {
  campaign_id: string;
  campaign_name: string;
  advertising_channel_type?: string;
  campaign_resource_name: string;
  budget_resource_name: string;
  current_amount_micros: number;
  proposed_amount_micros: number;
  delta_micros: number;
  share: number;
  relearn_clamped: boolean;
  bidding_strategy_type?: string;
  skip_reason?: string | null;
  write_status?: string | null;
  delivery_capped?: boolean;
  ceiling_bound?: boolean;
  baseline_amount_micros?: number | null;
  ceiling_max_micros?: number | null;
  /**
   * Set when this campaign is on its own MONTHLY budget rather than a share of
   * the account total. Reported explicitly rather than inferred from
   * `share === 0`, which is ambiguous — a campaign can legitimately be
   * allocated nothing. The table reads this to say which of the two modes a
   * campaign is on, instead of showing a misleading 0%.
   */
  fixed_monthly_usd?: number | null;
}

export interface SharedBudgetCampaign {
  campaign_id: string;
  campaign_name: string;
  advertising_channel_type?: string;
}

export interface SharedBudgetGroup {
  budget_resource_name: string;
  budget_id?: string;
  daily_budget_micros: number;
  campaign_count: number;
  campaigns: SharedBudgetCampaign[];
}

export interface AutonomousPacingLsa {
  /** 'same_account' when the reservation is Core's in-account set-aside, not a Planner line. */
  source?: string | null;
  set_aside_source?: string | null;
  customer_id?: string | null;
  /** False when Planner has no cycle row — leftover is unknown, not $0. */
  available?: boolean;
  budget_micros: number | null;
  spent_micros: number | null;
  remaining_micros: number | null;
  implied_daily_micros: number | null;
  /** Whole-dollar weekly budget to suggest. A rec for a human, never a write. */
  weekly_recommendation_micros?: number | null;
}

export interface AutonomousPacingApplyResponse {
  tenant_id: string;
  status: string;
  skip_reason?: string | null;
  budget_source: string;
  target_account_daily_micros: number;
  current_account_daily_micros: number;
  applied: number;
  skipped: number;
  errors: number;
  remainder_micros?: number;
  days_left?: number;
  blast_radius?: {
    requires_confirmation?: boolean;
    reasons?: string[];
  } | null;
  dry_run: boolean;
  adjustments: AutonomousPacingAdjustment[];
  shared_budgets?: SharedBudgetGroup[];
  ceiling_n?: number | null;
  /** Planner LSA leftover. Core does not write this account. */
  lsa?: AutonomousPacingLsa | null;
  /** Result of the separate, flag-dark LSA budget step on the same tick. */
  lsa_write?: AutonomousPacingLsaWrite | null;
  // True when SKIP_NON_POSITIVE_TARGET fires while the account is still
  // burning through a positive live daily. The cron intentionally does
  // NOT push $0 to a live campaign; the pacing card renders a "please
  // pause manually" strip so operators know the tenant needs a hand.
  requires_manual_pause?: boolean;
}

export interface AutonomousPacingLsaWrite {
  customer_id?: string | null;
  campaign_id?: string | null;
  budget_resource_name?: string | null;
  current_amount_micros: number | null;
  proposed_amount_micros: number | null;
  weekly_display_micros: number | null;
  planner_spent_micros: number | null;
  live_spent_micros: number | null;
  remaining_live_micros: number | null;
  days_left: number | null;
  /** success | dry_run | error | skipped */
  write_status: string;
  skip_reason?: string | null;
  error?: string | null;
  audit_id?: string | null;
}

export interface PacingCeiling {
  tenant_id: string;
  n: number | null;
  baseline_count: number;
}

export async function getPacingAccountScope(params: {
  tenantId: string;
}): Promise<PacingAccountScope> {
  const { data } = await apiClient.get<PacingAccountScope>(
    '/google-ads/monthly/budget-pacing/account-scope',
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function updatePacingAccountScope(params: {
  tenantId: string;
  adsCustomerId: string;
  lsaCustomerId?: string | null;
  campaignNameFilter?: string | null;
  includedCampaignIds?: string[] | null;
  /** Consent for Core to set this client's LSA budget; null leaves it unchanged. */
  lsaAutopace?: boolean | null;
  combinedTotals?: boolean | null;
}): Promise<PacingAccountScope> {
  const { data } = await apiClient.patch<PacingAccountScope>(
    '/google-ads/monthly/budget-pacing/account-scope',
    {
      ads_customer_id: params.adsCustomerId,
      lsa_customer_id: params.lsaCustomerId ?? '',
      campaign_name_filter: params.campaignNameFilter ?? '',
      included_campaign_ids: params.includedCampaignIds ?? null,
      lsa_autopace: params.lsaAutopace ?? null,
      combined_totals: params.combinedTotals ?? null,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

/**
 * The two Google monthly lines in Planner (Ads + LSA), their fixed total,
 * and which platforms carry a per-cycle override on the given cycle.
 * GET/PATCH /google-ads/monthly/budget-pacing/google-split
 */
export interface GoogleBudgetSplit {
  google_ads_monthly_dollars: number | null;
  google_lsa_monthly_dollars: number | null;
  total_monthly_dollars: number;
  has_lsa_line: boolean;
  has_ads_line: boolean;
  current_cycle_start: string | null;
  cycle_override_platforms: string[];
}

export interface GoogleBudgetSplitUpdate extends GoogleBudgetSplit {
  changed: Record<string, { from: number | null; to: number }>;
  cycle_rows_updated: string[];
  /** Server clock of the save; the Budgets page guards its post-save refetch with it. */
  saved_at: string;
}

export async function getGoogleBudgetSplit(params: {
  tenantId: string;
  billingPeriodStart?: string | null;
}): Promise<GoogleBudgetSplit> {
  const { data } = await apiClient.get<GoogleBudgetSplit>(
    '/google-ads/monthly/budget-pacing/google-split',
    {
      params: {
        tenant_id: params.tenantId,
        billing_period_start: params.billingPeriodStart ?? undefined,
      },
    },
  );
  return data;
}

/** Sets one Google line; the backend moves the difference onto the other so the total holds. */
export async function updateGoogleBudgetSplit(params: {
  tenantId: string;
  platform: 'Google LSA' | 'Google Ads';
  monthlyDollars: number;
  billingPeriodStart?: string | null;
  /** The total the operator saw; the server refuses if Planner's total moved since (optimistic lock). */
  expectedTotalDollars?: number | null;
}): Promise<GoogleBudgetSplitUpdate> {
  const { data } = await apiClient.patch<GoogleBudgetSplitUpdate>(
    '/google-ads/monthly/budget-pacing/google-split',
    {
      platform: params.platform,
      monthly_dollars: params.monthlyDollars,
      billing_period_start: params.billingPeriodStart ?? null,
      expected_total_dollars: params.expectedTotalDollars ?? null,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function updatePacingBudget(params: {
  tenantId: string;
  budgetUsd: number;
  billingPeriodStart: string;
}): Promise<{ budget_usd: number; google_ads_budget_usd: number }> {
  const { data } = await apiClient.patch(
    '/google-ads/monthly/budget-pacing/budget',
    {
      budget_usd: params.budgetUsd,
      billing_period_start: params.billingPeriodStart,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function updateAutonomousShares(params: {
  tenantId: string;
  shares: Record<string, number>;
}): Promise<{ shares: Record<string, number> }> {
  const { data } = await apiClient.patch(
    '/google-ads/monthly/budget-pacing/autonomous-shares',
    { shares: params.shares },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

/** One campaign's fixed monthly budget. `name` is stored alongside the id so a
 *  delete-and-recreate (new id, same name) is recoverable and the UI can label
 *  a row without a live Ads API call. */
export interface CampaignMonthlyBudget {
  monthly: number;
  name?: string;
}

/**
 * Set per-campaign MONTHLY budgets, so pacing stops deriving them from the
 * account total.
 *
 * With budgets saved, those campaigns hold their figure when the contract
 * moves; without, they stay a percentage share. An empty map clears them and
 * returns the client to proportional distribution.
 *
 * The server validates the total against the contracted monthly budget and
 * rejects an overrun — campaign budgets no longer derive from that figure, so
 * nothing downstream would catch it.
 */
export async function updateCampaignBudgets(params: {
  tenantId: string;
  budgets: Record<string, CampaignMonthlyBudget>;
}): Promise<{
  campaign_monthly_budgets: Record<string, CampaignMonthlyBudget>;
  total_monthly_dollars: number;
}> {
  const { data } = await apiClient.patch(
    '/google-ads/monthly/budget-pacing/campaign-budgets',
    { budgets: params.budgets },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function getPacingCeiling(params: {
  tenantId: string;
}): Promise<PacingCeiling> {
  const { data } = await apiClient.get<PacingCeiling>(
    '/google-ads/monthly/budget-pacing/ceiling',
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function updatePacingCeiling(params: {
  tenantId: string;
  n: number | null;
  resetBaselines?: boolean;
}): Promise<PacingCeiling> {
  const { data } = await apiClient.patch<PacingCeiling>(
    '/google-ads/monthly/budget-pacing/ceiling',
    { n: params.n, reset_baselines: params.resetBaselines === true },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

/** Per-client "Automated pacing: On/Off" (46062138). Staff-only on the backend. */
export interface AutomatedPacingState {
  tenant_id: string;
  enabled: boolean;
  changed_by?: string | null;
  changed_at?: string | null;
  reason?: string | null;
}

export async function getAutomatedPacing(params: {
  tenantId: string;
}): Promise<AutomatedPacingState> {
  const { data } = await apiClient.get<AutomatedPacingState>(
    '/google-ads/monthly/budget-pacing/automated-pacing',
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function updateAutomatedPacing(params: {
  tenantId: string;
  enabled: boolean;
  reason?: string | null;
}): Promise<AutomatedPacingState> {
  const { data } = await apiClient.put<AutomatedPacingState>(
    '/google-ads/monthly/budget-pacing/automated-pacing',
    { enabled: params.enabled, reason: params.reason || null },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export interface AutonomousPacingLastPlan {
  tenant_id: string;
  saved_at?: string | null;
  last_run_at?: string | null;
  /** Latest guarded re-run, if any; a freshness signal, not a tick. */
  last_rerun_at?: string | null;
  plan?: AutonomousPacingApplyResponse | null;
}

/** Last live tick snapshot. Cheap — no Google Ads call. */
export async function getLastAutonomousPacingPlan(params: {
  tenantId: string;
}): Promise<AutonomousPacingLastPlan> {
  const { data } = await apiClient.get<AutonomousPacingLastPlan>(
    '/google-ads/monthly/budget-pacing/autonomous-last-plan',
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

/** Dry-run pacing plan. Live writes are the Mon/Thu cron, not this request. */
export async function previewAutonomousPacing(params: {
  tenantId: string;
}): Promise<AutonomousPacingApplyResponse> {
  const { data } = await apiClient.post<AutonomousPacingApplyResponse>(
    '/google-ads/monthly/budget-pacing/autonomous-apply',
    null,
    {
      params: { tenant_id: params.tenantId },
      timeout: 60_000,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// P3.3 — Cluster refresh
// ---------------------------------------------------------------------------

export interface NewClusterCandidate {
  cluster_name: string;
  keywords: string[];
  landing_page: string | null;
  suggested_action: string;
}

export interface DeprecatedAdGroup {
  campaign_id: string;
  campaign_name: string;
  ad_group_id: string;
  ad_group_name: string;
  ad_group_resource_name: string;
  keywords: string[];
  suggested_action: string;
}

export interface ClusterRefreshResponse {
  tenant_id: string;
  mode: 'warn' | 'soft_auto';
  fresh_cluster_count: number;
  existing_ad_group_count: number;
  new_clusters: NewClusterCandidate[];
  deprecated_ad_groups: DeprecatedAdGroup[];
  approval_id: string | null;
  /**
   * CORE-K-04: True when this soft_auto call reused an existing
   * pending approval instead of queueing a duplicate. The frontend
   * badge tells the operator "same approval, not a new one".
   * Defaults to false when the backend omits the field (older
   * deployments), matching the pre-K-04 behaviour.
   */
  approval_reused?: boolean;
  skipped_reason: string | null;
}

export async function analyzeClusterRefresh(params: {
  tenantId: string;
  mode: 'warn' | 'soft_auto';
}): Promise<ClusterRefreshResponse> {
  const { data } = await apiClient.post<ClusterRefreshResponse>(
    '/google-ads/monthly/cluster-refresh/analyze',
    null,
    {
      params: {
        tenant_id: params.tenantId,
        mode: params.mode,
      },
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Type set-aside operator items (ai-docs/google-ads/type-set-aside.md). The
// item lives on the Management page; Budget pacing only shows a notice.
// ---------------------------------------------------------------------------

export interface TypeSetAsideItem {
  id: string;
  tenant_id: string;
  channel: string;
  status: string;
  requested_at?: string | null;
  payload: {
    title?: string;
    ask?: string;
    current_daily_budget_micros?: number | null;
    overdelivery_multiplier?: number | null;
    set_aside_daily_micros?: number | null;
    monthly_google_budget_usd?: number | null;
    budget_resource_names?: string[];
    [key: string]: unknown;
  };
}

export async function listTypeSetAsideItems(tenantId: string, status = 'pending'): Promise<TypeSetAsideItem[]> {
  const { data } = await apiClient.get<TypeSetAsideItem[]>('/google-ads/monthly/budget-pacing/type-set-aside', {
    params: { tenant_id: tenantId, status },
  });
  return data;
}

export async function approveTypeSetAside(params: {
  tenantId: string;
  requestId: string;
  monthlyBudgetDollars: number;
  resolvedBy?: string;
  writeCampaignBudget?: boolean;
}): Promise<{ ok: boolean; planner: { action: string; monthly_budget_dollars: number }; campaign_write: unknown }> {
  const { data } = await apiClient.post(
    `/google-ads/monthly/budget-pacing/type-set-aside/${params.requestId}/approve`,
    {
      monthly_budget_dollars: params.monthlyBudgetDollars,
      resolved_by: params.resolvedBy ?? 'operator',
      write_campaign_budget: params.writeCampaignBudget ?? true,
    },
    { params: { tenant_id: params.tenantId } },
  );
  return data;
}

export async function rejectTypeSetAside(params: { tenantId: string; requestId: string; resolvedBy?: string; note?: string }) {
  const { data } = await apiClient.post(
    `/google-ads/monthly/budget-pacing/type-set-aside/${params.requestId}/reject`,
    null,
    { params: { tenant_id: params.tenantId, resolved_by: params.resolvedBy ?? 'operator', note: params.note } },
  );
  return data as { ok: boolean; status: string | null };
}
